import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { FetchOptionsCallback } from 'global';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';

import { calendarApi, ReDateEventsInput } from './index';

export function useCalendar() {
  const { showNotification } = useNotifications();

  const [onComplete, { loading: isCompleting }] = useMutation(calendarApi.complete);

  const handleCompleteEvent = useCallback(
    async (ids: string[], fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onComplete({
          variables: { input: { ids } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully completed calendar events');

        return data.dealActivity.result.list;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong completing calendar events', { type: 'error' });
      }
    },
    [onComplete, showNotification]
  );

  const [onReDate, { loading: isReDating }] = useMutation(calendarApi.reDate);

  const handleReDateEvent = useCallback(
    async (input: ReDateEventsInput['input'], fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onReDate({
          variables: { input },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully re-dated calendar events');

        return data.dealActivity.result.list;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong re-dating calendar events', { type: 'error' });
      }
    },
    [onReDate, showNotification]
  );

  const [onMoveToTomorrow, { loading: isMovingToTomorrow }] = useMutation(calendarApi.moveToTomorrow);

  const handleMoveToTomorrowEvent = useCallback(
    async (ids: string[], fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onMoveToTomorrow({
          variables: { input: { ids } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully moved calendar events to tomorrow');

        return data.dealActivity.result.list;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong moving calendar events to tomorrow', { type: 'error' });
      }
    },
    [onMoveToTomorrow, showNotification]
  );

  const [onAddEventNote, { loading: isAddingEventNote }] = useMutation(calendarApi.addEventNote);

  const handleAddEventNote = useCallback(
    async (id: string, note: string, fetchOptions?: FetchOptionsCallback<typeof calendarApi>) => {
      try {
        const { data } = await onAddEventNote({
          variables: { input: { eventActivityId: id, content: note } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(calendarApi) : {}),
        });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully added note to calendar event');

        return data.dealActivity.result.entity;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong adding note to calendar event', { type: 'error' });
      }
    },
    [onAddEventNote, showNotification]
  );

  return {
    handleReDateEvent,
    handleAddEventNote,
    handleCompleteEvent,
    handleMoveToTomorrowEvent,
    isReDating,
    isCompleting,
    isAddingEventNote,
    isMovingToTomorrow,
  };
}
